import {
  vArray,
  vCountryAny,
  vDate,
  vDateUtcNoon,
  vEmail,
  vEmailOrEmpty,
  vFallback,
  vMilitaryTime,
  vNumber,
  vNumberToString,
  vObject,
  vObjectId,
  vOptional,
  vOptionalDef,
  type vOutput,
  vPhoneExt,
  vPhoneNumber,
  vPicklist,
  vPipe,
  vPostalCode,
  vString,
  vTransform,
} from '../validation/index.ts'
import type { GeoPoint } from './geo-point.ts'
import {
  flatbedHandlingTypes,
  locationAccessorialSchema,
} from './location-accessorial.ts'
import { type ShipType, shipTypes, vShipType } from './ship-type.ts'

export const locationRefNumTypes = ['other'] as const
export const locationTypes = ['origin', 'destination', 'stop'] as const
export const stopTypes = ['pickup', 'delivery', 'both'] as const
export const stopDateTypes = ['on', 'by', 'between'] as const
export type LocationType = (typeof locationTypes)[number]
export type StopType = (typeof stopTypes)[number]
export type StopDateType = (typeof stopDateTypes)[number]
export type LocationRefNumType = (typeof locationRefNumTypes)[number]

const loadLocationRefNumSchema = vObject({
  type: vPicklist(locationRefNumTypes),
  value: vPipe(
    vNumberToString(),
    vTransform(v => v ?? ''),
  ),
})
export const loadLocationSchema = vObject({
  addressBookId: vOptional(vObjectId()),
  accountLocationId: vOptional(vObjectId()),
  sequence: vNumber(),
  shipType: vShipType(),
  stopType: vPicklist(stopTypes),
  company: vOptional(vString()),
  address: vOptional(vString()),
  address2: vOptional(vString()),
  adminArea3: vOptional(vString()),
  city: vString(),
  state: vString(),
  postalCode: vPostalCode(),
  country: vCountryAny,
  timezone: vFallback(vString(), ''),
  stopDate: vOptional(vDate()),
  stopDateType: vPicklist(stopDateTypes),
  opensAt: vOptional(vString()),
  closesAt: vOptional(vString()),
  instructions: vOptional(vString()),
  internalNotes: vOptional(vString()),
  accessorials: vOptional(vArray(locationAccessorialSchema)),
  contactName: vOptional(vString()),
  contactEmail: vOptional(vEmail),
  contactPhone: vOptional(vPhoneNumber()),
  contactPhoneExt: vOptional(vString()),
  type: vPicklist(locationTypes),
  refNums: vOptional(vArray(loadLocationRefNumSchema)),
  flatbedHandlingType: vOptional(vPicklist(flatbedHandlingTypes)),
})
export type LoadLocationDto = vOutput<typeof loadLocationSchema>
export type LocationRefNum = vOutput<typeof loadLocationRefNumSchema>
export type LoadLocation = Omit<LoadLocationDto, 'timezone' | 'shipType'> & {
  shipType?: ShipType
  lat: number
  lng: number
  geo?: GeoPoint
  timezone: string
}

const apiLocationSharedFields = {
  accountLocationId: vOptional(vObjectId()),
  addressBookId: vOptional(vObjectId()),
  sequence: vNumber(),
  type: vPicklist(locationTypes),
  stopType: vPicklist(stopTypes),
  address2: vOptional(vString()),
  adminArea3: vOptional(vString()),
  city: vString(),
  state: vString(),
  postalCode: vPostalCode(),
  lat: vNumber(),
  lng: vNumber(),
  timezone: vString(),
  stopDate: vOptional(vDateUtcNoon()),
  stopDateType: vOptionalDef(vPicklist(stopDateTypes), 'on'),
  opensAt: vOptional(vMilitaryTime()),
  closesAt: vOptional(vMilitaryTime()),
  instructions: vOptional(vString()),
  internalNotes: vOptional(vString()),
  accessorials: vOptional(vArray(locationAccessorialSchema)),
  contactName: vOptional(vString()),
  contactEmail: vOptional(vEmailOrEmpty),
  contactPhone: vOptional(vPhoneNumber()),
  contactPhoneExt: vOptional(vPhoneExt()),
  refNums: vOptional(vArray(loadLocationRefNumSchema)),
  shipType: vOptional(vPicklist(shipTypes)),
  flatbedHandlingType: vOptional(vPicklist(flatbedHandlingTypes)),
}

export const apiLoadLocationSchema = vObject({
  ...apiLocationSharedFields,
  company: vString(),
  address: vString(),
  country: vCountryAny,
})

export const apiQuoteRequestLocationSchema = vObject({
  ...apiLocationSharedFields,
  company: vOptional(vString()),
  address: vOptional(vString()),
  country: vOptionalDef(vCountryAny, 'us'),
})

export type ApiLoadLocationDTO = vOutput<typeof apiLoadLocationSchema>
